import { useEffect } from "react";

export type Shortcut = {
  key: string;
  meta?: boolean;
  shift?: boolean;
  ctrl?: boolean;
  alt?: boolean;
  description?: string;
  handler: (e: KeyboardEvent) => void;
};

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
};

/**
 * Bind a list of keyboard shortcuts. Skips when the user is typing into an
 * input/textarea/contenteditable. Pass `force: true` on a shortcut to override.
 */
export function useKeyboardShortcuts(
  shortcuts: Array<Shortcut & { force?: boolean }>,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const typing = isTypingTarget(e.target);
      for (const s of shortcuts) {
        if (typing && !s.force) continue;
        if (s.key.toLowerCase() !== e.key.toLowerCase()) continue;
        if ((s.meta ?? false) !== (e.metaKey || e.ctrlKey)) continue;
        if ((s.shift ?? false) !== e.shiftKey) continue;
        if ((s.alt ?? false) !== e.altKey) continue;
        if ((s.ctrl ?? false) && !e.ctrlKey) continue;
        e.preventDefault();
        s.handler(e);
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shortcuts, enabled]);
}

/**
 * Two-key chord ("g i" → go inbox). Holds the first key for 1s.
 */
export function useChordShortcuts(
  chords: Array<{ keys: [string, string]; handler: () => void; description?: string }>,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    let pendingKey: string | null = null;
    let timer: number | null = null;
    const reset = () => {
      pendingKey = null;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (!pendingKey) {
        const candidate = chords.find((c) => c.keys[0] === k);
        if (candidate) {
          pendingKey = k;
          e.preventDefault();
          timer = window.setTimeout(reset, 1000);
        }
        return;
      }
      const match = chords.find((c) => c.keys[0] === pendingKey && c.keys[1] === k);
      if (match) {
        e.preventDefault();
        match.handler();
      }
      reset();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      reset();
    };
  }, [chords, enabled]);
}
